import { ReviewStepProps } from '@backstage/plugin-scaffolder-react';
import {
  ParsedTemplateSchema,
  ReviewState,
} from '@backstage/plugin-scaffolder-react/alpha';
import {
  FormDataWithExtras,
  getExtra,
  getPreviewOptions,
  ScorringKind,
} from '../extensions';
import { Button, styled } from '@material-ui/core';
import { QuestionnaireReview } from './QuestionnaireResults/QuestionnaireReview';
import { OutcomesReview } from './OutcomeReview';
import { StepCollection } from './QuestionnaireResults/schema';
import { CustomReview } from './CustomReview.tsx';
import { useMemo, useState } from 'react';
import { Confirmation } from './Confirmation.tsx';
import { clearFormState } from '../PersistFormState/form-state-manager';

const Footer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '1rem',
});
const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  width: '100%',
});

type PreviewKind = 'outcomes' | 'questionnaire' | 'preview' | undefined;
const questionnaires: ScorringKind[] = [
  'like-love',
  'ladder',
  'group',
  'percent',
];

function resolvePreviewStep(steps: ParsedTemplateSchema[]): PreviewKind {
  const options = getPreviewOptions(steps);
  const { scoring } = options ?? {};
  if (questionnaires.includes(scoring as ScorringKind)) {
    return 'questionnaire';
  }
  return scoring as PreviewKind;
}

const shouldConfirm = (reviewKind: string) => {
  return ['questionnaire', 'outcomes', 'preview'].includes(reviewKind!);
};

export function Review(props: ReviewStepProps) {
  const formData = props.formData as FormDataWithExtras;
  const steps = props.steps as StepCollection;
  const { handleCreate, handleBack } = props;
  const previewOptions = getPreviewOptions(steps);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const openConfirmation = () => setIsConfirmOpen(true);
  const closeConfirmation = () => setIsConfirmOpen(false);

  const reviewKind = useMemo(() => resolvePreviewStep(steps), [steps]);
  const withStateCleanup = (callback: () => void) => {
    const storeKey = getExtra<string>('store_key');
    if (storeKey) {
      clearFormState(storeKey);
    }
    callback();
  };

  const onCreate = () => {
    if (shouldConfirm(reviewKind!)) {
      openConfirmation();
      return;
    }
    withStateCleanup(handleCreate);
  };

  const content = () => {
    switch (reviewKind) {
      case 'outcomes': {
        return (
          <OutcomesReview
            formData={formData}
            steps={steps}
            onSubmit={onCreate}
          />
        );
      }
      case 'questionnaire': {
        return (
          <QuestionnaireReview
            formData={formData}
            steps={steps}
            onSubmit={onCreate}
          />
        );
      }
      case 'preview': {
        return (
          <CustomReview formData={formData} steps={steps} onSubmit={onCreate} />
        );
      }
      default: {
        return (
          <ReviewState
            schemas={props.steps as ParsedTemplateSchema[]}
            formState={props.formData}
          />
        );
      }
    }
  };

  return (
    <Root>
      {content()}
      {isConfirmOpen && (
        <Confirmation
          onConfirm={() => {
            closeConfirmation();
            withStateCleanup(handleCreate);
          }}
          onCancel={() => setIsConfirmOpen(false)}
        />
      )}
      <Footer>
        <Button onClick={handleBack}> Back </Button>
        <Button onClick={onCreate} variant="contained" color="primary">
          {previewOptions?.submitText || 'Create'}
        </Button>
      </Footer>
    </Root>
  );
}
